<template>
  <div class="modal" :class="{ 'is-active': is_active }">
    <div class="modal-background" @click="close_modal"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ is_editing ? $t("edit task") : $t("create task") }}
        </p>
        <button class="delete" @click="close_modal"></button>
      </header>
      <section class="modal-card-body">
        <div class="field">
          <label class="label">{{ $t("title") }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="meta.title"
              :placeholder="$t('title')"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("description") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="meta.description"
              :placeholder="$t('description')"
              rows="3"
            ></textarea>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("status") }}</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="meta.status">
                    <option value="pending">{{ $t("pending") }}</option>
                    <option value="in_progress">{{ $t("in progress") }}</option>
                    <option value="completed">{{ $t("completed") }}</option>
                    <option value="cancelled">{{ $t("cancelled") }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("priority") }}</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="meta.priority">
                    <option value="low">{{ $t("low") }}</option>
                    <option value="medium">{{ $t("medium") }}</option>
                    <option value="high">{{ $t("high") }}</option>
                    <option value="critical">{{ $t("critical") }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <div class="field">
              <generic-dropdown
                :title="$t('assigned user')"
                :placeholder="$t('select user')"
                url="/access/user/catalog"
                name_field="username"
                value_field="user_id"
                @selected="set_assigned_user"
                ref="user_dropdown"
                :is_multiple="false"
              />
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t("due date") }}</label>
              <div class="control">
                <input class="input" type="date" v-model="meta.due_date" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="field" v-if="is_editing">
              <label class="label">{{ $t("completion percentage") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="number"
                  min="0"
                  max="100"
                  v-model="meta.completion_percentage"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot">
        <button
          class="button is-rounded is-primary"
          @click="save"
          :disabled="is_form_invalid"
        >
          <span class="icon">
            <i class="fa fa-save"></i>
          </span>
          <span>{{ $t("save") }}</span>
        </button>
        <button class="button is-rounded ml-3" @click="close_modal">
          <span class="icon">
            <i class="fa fa-close"></i>
          </span>
          <span>{{ $t("cancel") }}</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { put, post, get, error_message, success_message } from "@/utils";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";

export default {
  name: "TaskModal",
  components: { GenericDropdown },
  data() {
    return {
      is_active: false,
      is_editing: false,
      meta: {
        title: "",
        description: "",
        status: "pending",
        priority: "medium",
        due_date: "",
        assigned_user_id: null,
        completion_percentage: 0,
        settings: {},
      },
    };
  },
  computed: {
    is_form_invalid() {
      let v = this;
      return !v.meta.title || !v.meta.description || !v.meta.due_date;
    },
  },
  methods: {
    show_modal() {
      let v = this;
      v.set_initial_state();
      v.is_editing = false;
      v.is_active = true;
    },
    edit_modal(uuid) {
      let v = this;
      v.set_initial_state();
      v.is_editing = true;
      v.is_active = true;
      v.load_data(uuid);
    },
    close_modal() {
      let v = this;
      v.set_initial_state();
      v.is_active = false;
    },
    set_initial_state() {
      let v = this;
      v.meta = {
        title: "",
        description: "",
        status: "pending",
        priority: "medium",
        due_date: "",
        assigned_user_id: null,
        completion_percentage: 0,
        settings: {},
      };
      if (v.$refs.user_dropdown) {
        v.$refs.user_dropdown.clear();
      }
    },
    load_data(uuid) {
      let v = this;
      get("/task", { uuid: uuid })
        .then((response) => {
          let task = response;
          v.meta = {
            uuid: task.uuid,
            title: task.title,
            description: task.description,
            status: task.status,
            priority: task.priority,
            due_date: task.due_date,
            assigned_user_id: task.assigned_user_id,
            completion_percentage: task.completion_percentage || 0,
            settings: task.settings || {},
          };

          // Set the assigned user in the dropdown if exists
          if (task.assigned_user_id && v.$refs.user_dropdown) {
            v.$refs.user_dropdown.set_value(task.assigned_user_id);
          }
        })
        .catch((error) => {
          error_message(error);
        });
    },
    set_assigned_user(user) {
      let v = this;
      if (user) {
        v.meta.assigned_user_id = user.user_id;
      } else {
        v.meta.assigned_user_id = null;
      }
    },
    save() {
      let v = this;
      let request_method = v.is_editing ? post : put;
      let success_message_text = v.is_editing
        ? v.$t("task updated successfully")
        : v.$t("task created successfully");

      request_method("/task", v.meta)
        .then(() => {
          success_message(success_message_text);
          v.$emit("success");
          v.close_modal();
        })
        .catch((error) => {
          error_message(error);
        });
    },
  },
};
</script>

<style scoped></style>
